import { Injectable } from '@nestjs/common';
import { PrismaService } from '@cart/api-data-access';
import { StoreContextService } from './store-context.service';

export interface SitemapEntry {
  path: string;
  lastModified?: string;
  changeFrequency?: 'daily' | 'weekly' | 'monthly';
  priority?: string;
}

/**
 * Per-tenant sitemap data.
 *
 * Returns **paths, not URLs**, deliberately. The API is reached through a
 * reverse proxy and cannot know which of a store's domains a given visitor
 * used — `Host` here may be the internal upstream address. Only the SSR host
 * knows the public origin (it already derives it from `x-forwarded-host` for
 * canonicals), so it is the one that turns these into absolute URLs. Emitting
 * absolute URLs from here would produce a sitemap full of unreachable
 * addresses, which is worse than no sitemap.
 */
@Injectable()
export class StoreSitemapService {
  constructor(
    private readonly prisma: PrismaService,
    private readonly storeContext: StoreContextService,
  ) {}

  async entries(): Promise<SitemapEntry[]> {
    const storeId = this.storeContext.storeId();

    const [products, categories] = await Promise.all([
      this.prisma.product.findMany({
        // `active` (mapped to the isActive column), not `isActive` — and there
        // is no soft-delete column on Product, so `active` is the only
        // visibility flag there is.
        where: { storeId, active: true },
        select: { slug: true, updatedAt: true },
        orderBy: { updatedAt: 'desc' },
        // Sitemaps cap at 50k URLs; this also bounds the query on a large
        // catalogue. A store past this needs a sitemap index, not a bigger page.
        take: 5000,
      }),
      // Category does use `isActive`; the two models genuinely differ.
      this.prisma.category.findMany({
        where: { storeId, isActive: true },
        select: { slug: true },
      }),
    ]);

    return [
      { path: '/', changeFrequency: 'daily', priority: '1.0' },
      { path: '/products', changeFrequency: 'daily', priority: '0.9' },
      ...categories.map((c): SitemapEntry => ({
        path: `/products?category=${encodeURIComponent(c.slug)}`,
        changeFrequency: 'weekly',
        priority: '0.7',
      })),
      ...products.map((p): SitemapEntry => ({
        path: `/products/${encodeURIComponent(p.slug)}`,
        lastModified: p.updatedAt?.toISOString(),
        changeFrequency: 'weekly',
        priority: '0.8',
      })),
    ];
  }
}
